import React from "react";
import { styled } from "@mui/material/styles";
import { FormControl, InputLabel, Paper, TextField, InputAdornment } from "@mui/material";
import { useTranslation } from "react-i18next";
import debounce from "lodash/debounce";
import { serviceContainer } from "services/serviceContainer";
import { IAddressSuggestionEntity, IExtendedAddressEntity } from "models/Address.model";
import Autocomplete from "@mui/material/Autocomplete";
import trim from "lodash/trim";
import get from "lodash/get";
import { AutocompleteRenderInputParams } from "@mui/material/Autocomplete/Autocomplete";
import { css } from "@emotion/react";
import FontIcon from "@odl/core/components/DataDisplay/FontIcon";

type Props = {
  address: IExtendedAddressEntity | null;
  required?: boolean;
  onAddressChange: (address: IExtendedAddressEntity | null) => void;
  onLoadingAddress?: (isLoading: boolean) => void;
};

/**
 * Controlled address lookup input
 *
 * @param address
 * @param onAddressChange
 * @param onLoadingAddress
 * @constructor
 */
const AddressLookup: React.FC<Props> = ({ address, required = false, onAddressChange, onLoadingAddress }) => {
  const { t } = useTranslation();

  const [searchInputValue, setSearchInputValue] = React.useState(address?.fullAddress || "");
  const [isOptionListOpen, setIsOptionListOpen] = React.useState(false);
  const [addressSuggestions, setAddressSuggestions] = React.useState<IAddressSuggestionEntity[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const isAutocompleteInputFocusedRef = React.useRef(false);

  const clearAddressSearch = React.useCallback(() => {
    setSearchInputValue("");
    onAddressChange(null);
  }, [setSearchInputValue, onAddressChange]);

  const debouncedSearch = React.useMemo(
    () =>
      debounce(async (keywords: string) => {
        try {
          const options = await serviceContainer.cradle.addressCheckerService.search(keywords);

          // If autocomplete input is not focused anymore, we drop the result
          if (!isAutocompleteInputFocusedRef.current) {
            return;
          }

          setAddressSuggestions(options);
          setIsOptionListOpen(true);
        } catch (e) {
          serviceContainer.cradle.logger.error(e);
        }
      }, 1000),
    []
  );

  const debouncedLookupAddress = React.useCallback((keywords: string) => debouncedSearch(keywords), [debouncedSearch]);

  const handleSelectAddressSuggestion = React.useCallback(
    async (event: React.ChangeEvent<{}>, suggestion: IAddressSuggestionEntity | null, reason: string) => {
      setIsOptionListOpen(false);

      if (!suggestion) {
        clearAddressSearch();
        return;
      }

      setIsLoading(true);
      try {
        const addressIdentifier = suggestion.identifier;
        const address = await serviceContainer.cradle.addressCheckerService.lookup(addressIdentifier);

        onAddressChange(address);
      } catch (e) {
        serviceContainer.cradle.logger.error(e);
      } finally {
        setIsLoading(false);
      }
    },
    [clearAddressSearch, onAddressChange]
  );

  const handleSearchInputChange = React.useCallback(
    (event: any, value: string, reason: string) => {
      if (reason === "reset") {
        return;
      }
      setSearchInputValue(value);

      if (!value || value.length < 2) {
        return;
      }

      debouncedLookupAddress(value);
    },
    [debouncedLookupAddress]
  );

  const handleAddressSearchInputBlur = React.useCallback(
    (evt: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setIsOptionListOpen(false);

      // If input value is empty, clear the address details
      if (!trim(evt.target.value)) {
        clearAddressSearch();
      }
    },
    [clearAddressSearch]
  );

  const handleInputOnBlur = React.useCallback(
    (evt: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, params: AutocompleteRenderInputParams) => {
      isAutocompleteInputFocusedRef.current = false;

      const inputProps: any = params.inputProps;
      if (inputProps.onBlur) {
        inputProps.onBlur(evt);
      }

      handleAddressSearchInputBlur(evt);
    },
    [handleAddressSearchInputBlur]
  );

  const handleInputOnFocus = React.useCallback(
    (evt: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, params: AutocompleteRenderInputParams) => {
      isAutocompleteInputFocusedRef.current = true;

      const inputProps: any = params.inputProps;
      if (inputProps.onFocus) {
        inputProps.onFocus(evt);
      }
    },
    []
  );
  // Effect: observe loading state
  React.useEffect(() => {
    if (onLoadingAddress) {
      onLoadingAddress(isLoading);
    }
  }, [isLoading, onLoadingAddress]);

  // Effect: observe address
  React.useEffect(() => {
    setSearchInputValue(address?.fullAddress || "");
  }, [address]);

  return (
    <FormControl
      fullWidth={true}
      error={false}
      hiddenLabel={true}
      required={true}
      disabled={false}
      data-testid={"AddressLookup"}
    >
      <Autocomplete
        data-testid="AddressInputAutocomplete"
        freeSolo={false}
        openOnFocus={false}
        selectOnFocus={false}
        blurOnSelect={true}
        onChange={handleSelectAddressSuggestion}
        onInputChange={handleSearchInputChange}
        inputValue={searchInputValue}
        open={isOptionListOpen}
        PaperComponent={StyledAutocompletePopupPaper}
        ListboxComponent={StyledAutocompleteListbox}
        renderInput={(params) => {
          return (
            <div ref={params.InputProps.ref}>
              <InputLabel required={required} shrink={false} htmlFor={get(params.inputProps, "id")}>
                {t(`Address of the building site`)}
              </InputLabel>

              <StyledTextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FontIcon name="i-travel-location" />
                    </InputAdornment>
                  ),
                }}
                inputProps={params.inputProps}
                type="search"
                autoComplete="off"
                onBlur={(evt) => handleInputOnBlur(evt, params)}
                onFocus={(evt) => handleInputOnFocus(evt, params)}
              />
            </div>
          );
        }}
        options={addressSuggestions}
        getOptionLabel={(option) => option.fullAddress}
      />
    </FormControl>
  );
};

const StyledTextField = styled(TextField)(
  ({ theme }) => css`
    & > input {
      padding-left: 10px;
      color: ${theme.palette.objective.dark.night};
      font-weight: 400;
      font-size: 14px;
    }
    & > fieldset {
      border-radius: 8px;
    }

    width: 100%;
  `
);

const StyledAutocompletePopupPaper = styled(Paper)(
  ({ theme }) => css`
    box-shadow: ${theme.shadows[6]};

    .MuiAutocomplete-noOptions {
      font-size: 14px;
    }
  `
);

const StyledAutocompleteListbox = styled("ul")(
  ({ theme }) => css`
    & > li {
      border-left: 4px solid transparent;
      padding: 8px 8px;
      font-size: 14px;
    }

    & > li:hover {
      border-left: 4px solid ${theme.palette.primary.main};
      background: ${theme.palette.objective.blue.light};
    }
  `
);

export default AddressLookup;
